/**
 * TermsContent — shared terms body JSX
 * Used by both TermsOverlay (existing modal) and WelcomeWizard (step 1).
 * Renders only the content; callers provide the scroll container.
 */

import React from 'react';
import { Box, Typography, Sheet, Divider } from '@mui/joy';
import { Warning, Shield, Copyright } from '@mui/icons-material';

export default function TermsContent() {
  return (
    <>
      {/* Astrology Disclaimer */}
      <Sheet variant="soft" color="warning" sx={{ p: 2, borderRadius: 'sm', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Warning sx={{ color: 'warning.600', mt: 0.25 }} />
          <Box>
            <Typography level="title-sm" sx={{ fontWeight: 700, mb: 0.5 }}>
              ASTROLOGY DISCLAIMER
            </Typography>
            <Typography level="body-sm">
              ShreemAstro provides astrological guidance for <strong>entertainment and educational purposes only</strong>.
              Our predictions are not a substitute for professional medical, legal, financial, or psychological advice.
            </Typography>
          </Box>
        </Box>
      </Sheet>

      <Typography level="h3">
        <Shield sx={{ fontSize: 20 }} />
        1. Acceptance of Terms
      </Typography>
      <p>
        By accessing or using ShreemAstro ("the Service"), you agree to be bound by these Terms and Conditions.
        If you do not agree to these terms, you must immediately discontinue use of the Service.
      </p>

      <Typography level="h3">2. Nature of Astrological Services</Typography>
      <p>
        ShreemAstro provides Vedic astrology readings, birth chart analysis, dasha predictions, and related services based on
        traditional astrological principles. You acknowledge and agree that:
      </p>
      <ul>
        <li>Astrology is an ancient interpretive art and not an exact science</li>
        <li>Predictions are probabilistic insights, not guaranteed outcomes</li>
        <li>Results depend on accuracy of birth details provided by you</li>
        <li>Free will and personal choices influence life events</li>
        <li>No astrological reading can predict or prevent specific events with certainty</li>
      </ul>

      <Typography level="h3">3. Limitation of Liability &amp; Disclaimer</Typography>
      <p>
        <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
      </p>
      <ul>
        <li>ShreemAstro provides the Service "AS IS" without warranties of any kind</li>
        <li>We are NOT responsible for decisions made based on astrological readings</li>
        <li>We shall NOT be liable for any direct, indirect, incidental, consequential, or punitive damages</li>
        <li>You assume all risks associated with use of astrological guidance</li>
        <li>Our maximum liability is limited to the amount paid for subscription (if any)</li>
      </ul>
      <p>
        <strong>Professional Advice:</strong> ShreemAstro does NOT provide medical diagnosis, legal counsel, financial planning,
        or mental health treatment. Always consult qualified professionals for such matters.
      </p>

      <Typography level="h3">4. Data Collection &amp; Usage</Typography>
      <p>By using ShreemAstro, you consent to our collection and use of the following data:</p>
      <ul>
        <li><strong>Birth Details:</strong> Date, time, location (for chart calculations)</li>
        <li><strong>Account Information:</strong> Email address, name, phone number (optional)</li>
        <li><strong>Usage Data:</strong> IP address, device info, browsing patterns (for security &amp; analytics)</li>
        <li><strong>Saved Charts:</strong> Birth profiles of you and family members</li>
        <li><strong>Communication History:</strong> Chat messages, support tickets</li>
      </ul>
      <p>
        <strong>Data Usage:</strong> Your data is used exclusively for providing astrological services, account management,
        security, and improving our platform. We comply with IT Act 2000 (India), GDPR (EU), and CCPA (California) regulations.
      </p>
      <p>
        <strong>Data Sharing:</strong> We do NOT sell your data to third parties. Data may be shared only with:
      </p>
      <ul>
        <li>Service providers (hosting, payment processing) under strict confidentiality</li>
        <li>Law enforcement if legally required</li>
      </ul>

      <Typography level="h3">5. Communication Consent</Typography>
      <p>You agree that ShreemAstro may contact you via email for:</p>
      <ul>
        <li>Account verification, password resets, security alerts</li>
        <li>Subscription confirmations, payment receipts, renewal reminders</li>
        <li>Important service updates or changes to Terms</li>
        <li>Astrological alerts (transit notifications, dasha changes)</li>
      </ul>
      <p>If you provide a phone number, you consent to receiving SMS for critical account/security matters only.</p>
      <p>
        <strong>Marketing:</strong> We will send promotional emails only if you opt-in via the checkbox below.
        You can unsubscribe anytime.
      </p>

      <Typography level="h3">
        <Copyright sx={{ fontSize: 20 }} />
        6. Intellectual Property &amp; Copyright
      </Typography>
      <p><strong>© ShreemAstro 2026. All Rights Reserved.</strong></p>
      <p>All content on ShreemAstro, including but not limited to:</p>
      <ul>
        <li>Birth charts (North Indian, South Indian, divisional charts)</li>
        <li>Astrological analysis, predictions, remedies</li>
        <li>AI-generated insights, life area overviews</li>
        <li>Yoga analysis, dasha timelines, transit reports</li>
        <li>Text, graphics, logos, software, algorithms</li>
      </ul>
      <p>
        ...is the exclusive intellectual property of ShreemAstro and protected under Indian Copyright Act 1957,
        Digital Millennium Copyright Act (DMCA), and international copyright treaties.
      </p>
      <p><strong>STRICTLY PROHIBITED:</strong></p>
      <ul>
        <li>Copying, reproducing, or distributing any content</li>
        <li>Taking screenshots or screen recordings of charts/analysis</li>
        <li>Sharing login credentials with others</li>
        <li>Using content for commercial purposes</li>
        <li>Reverse engineering or scraping our platform</li>
      </ul>
      <p>
        <strong>Permitted Use:</strong> You may view content solely for your personal, non-commercial use.
        Violations may result in account termination and legal action.
      </p>

      <Typography level="h3">7. User Responsibilities</Typography>
      <ul>
        <li>Provide accurate birth details for reliable readings</li>
        <li>Keep your account credentials secure</li>
        <li>Not share your account with others</li>
        <li>Not use the Service for unlawful purposes</li>
        <li>Not attempt to hack, disrupt, or abuse the platform</li>
      </ul>

      <Typography level="h3">8. Account Termination</Typography>
      <p>ShreemAstro reserves the right to suspend or terminate your account immediately without notice if you:</p>
      <ul>
        <li>Violate these Terms &amp; Conditions</li>
        <li>Engage in fraudulent activity</li>
        <li>Abuse trial/free features (multiple accounts, etc.)</li>
        <li>Share copyrighted content publicly</li>
      </ul>

      <Typography level="h3">9. Subscription &amp; Refunds</Typography>
      <ul>
        <li>Free trials are one-time per user (tracked by IP/email/phone)</li>
        <li>Paid subscriptions auto-renew unless cancelled</li>
        <li>Refunds available within trial period only (see Cancellation Policy)</li>
        <li>No refunds for partially used subscription periods</li>
      </ul>

      <Typography level="h3">10. Privacy &amp; Data Rights</Typography>
      <p>You have the right to:</p>
      <ul>
        <li>Access your personal data</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your account and data</li>
        <li>Export your data (birth profiles, chat history)</li>
      </ul>
      <p>
        Contact us at <strong>support@shreemastro.com</strong> for data requests. We will respond within 30 days.
      </p>

      <Typography level="h3">11. Changes to Terms</Typography>
      <p>
        ShreemAstro may update these Terms at any time. If changes are material, we will notify you via email and
        require re-acceptance. Continued use after notification constitutes acceptance.
      </p>

      <Typography level="h3">12. Governing Law &amp; Jurisdiction</Typography>
      <p>
        These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction
        of courts in India.
      </p>

      <Divider sx={{ my: 2 }} />

      <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Version 1.0 | Effective Date: January 1, 2026 | Last Updated: January 1, 2026
      </Typography>
    </>
  );
}
